import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import type { EmailShareRecipientContract } from '@/contracts/share'
import { Mail, Plus, UserRoundCheck, X } from 'lucide-react'
import { useState } from 'react'

type EmailRecipientPickerProps = {
  toEmails: string[]
  ccEmails: string[]
  salesRecipients: EmailShareRecipientContract[]
  onToEmailsChange: (emails: string[]) => void
  onCcEmailsChange: (emails: string[]) => void
}

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

function normalizeEmailList(value: string) {
  return value
    .split(/[\n,;]/)
    .map((item) => item.trim())
    .filter(Boolean)
}

function mergeEmails(current: string[], next: string[]) {
  const seen = new Set(current.map((email) => email.toLocaleLowerCase()))
  const merged = [...current]
  for (const email of next) {
    const key = email.toLocaleLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    merged.push(email)
  }
  return merged
}

export function EmailRecipientPicker({
  toEmails,
  ccEmails,
  salesRecipients,
  onToEmailsChange,
  onCcEmailsChange,
}: EmailRecipientPickerProps) {
  const [toInput, setToInput] = useState('')
  const [ccInput, setCcInput] = useState('')

  function addToEmails() {
    const next = normalizeEmailList(toInput)
    if (next.length === 0) return
    onToEmailsChange(mergeEmails(toEmails, next))
    setToInput('')
  }

  function addCcEmails() {
    const next = normalizeEmailList(ccInput)
    if (next.length === 0) return
    onCcEmailsChange(mergeEmails(ccEmails, next))
    setCcInput('')
  }

  function toggleSalesRecipient(email: string) {
    const exists = ccEmails.some((item) => item.toLocaleLowerCase() === email.toLocaleLowerCase())
    onCcEmailsChange(exists ? ccEmails.filter((item) => item !== email) : mergeEmails(ccEmails, [email]))
  }

  return (
    <div className="space-y-4">
      <RecipientInput
        label="收件人"
        description="可貼上多筆 Email，以逗號、分號或換行分隔。"
        value={toInput}
        onChange={setToInput}
        onAdd={addToEmails}
        placeholder="customer@example.com, buyer@example.com"
      />
      <EmailChips emails={toEmails} onRemove={(email) => onToEmailsChange(toEmails.filter((item) => item !== email))} />

      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <Label>CC 業務 Email</Label>
            <p className="mt-1 text-sm text-slate-500">
              可指定多位業務作為副本，也可手動加入外部窗口。
            </p>
          </div>
          <Badge variant="outline">dedupe enabled</Badge>
        </div>

        <div className="mt-4 grid gap-3 xl:grid-cols-[minmax(0,1fr)_240px]">
          <div>
            <div className="flex gap-2">
              <Input
                value={ccInput}
                onChange={(event) => setCcInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    addCcEmails()
                  }
                }}
                placeholder="sales@example.com"
              />
              <Button type="button" variant="outline" onClick={addCcEmails}>
                <Plus className="size-4" />
                加入
              </Button>
            </div>
            <div className="mt-3">
              <EmailChips emails={ccEmails} onRemove={(email) => onCcEmailsChange(ccEmails.filter((item) => item !== email))} />
            </div>
          </div>

          <div className="min-h-0 rounded-md border border-slate-200 bg-slate-50">
            <div className="border-b border-slate-200 px-3 py-2 text-xs font-semibold text-slate-500">
              業務清單
            </div>
            <ScrollArea className="h-40" viewportClassName="p-2">
              <div className="space-y-1">
                {salesRecipients.map((recipient) => {
                  const selected = ccEmails.some(
                    (email) => email.toLocaleLowerCase() === recipient.email.toLocaleLowerCase(),
                  )
                  return (
                    <button
                      key={recipient.id}
                      type="button"
                      onClick={() => toggleSalesRecipient(recipient.email)}
                      className={[
                        'flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors',
                        selected
                          ? 'bg-emerald-100 text-emerald-950'
                          : 'text-slate-600 hover:bg-white',
                      ].join(' ')}
                    >
                      <UserRoundCheck className="size-4 shrink-0" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">{recipient.name}</span>
                        <span className="block truncate text-xs opacity-75">{recipient.email}</span>
                      </span>
                    </button>
                  )
                })}
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>
    </div>
  )
}

function RecipientInput({
  label,
  description,
  value,
  placeholder,
  onChange,
  onAdd,
}: {
  label: string
  description: string
  value: string
  placeholder: string
  onChange: (value: string) => void
  onAdd: () => void
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <Label>{label}</Label>
      <p className="mt-1 text-sm text-slate-500">{description}</p>
      <div className="mt-3 flex gap-2">
        <Input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              onAdd()
            }
          }}
          placeholder={placeholder}
        />
        <Button type="button" onClick={onAdd}>
          <Plus className="size-4" />
          加入
        </Button>
      </div>
    </div>
  )
}

function EmailChips({
  emails,
  onRemove,
}: {
  emails: string[]
  onRemove: (email: string) => void
}) {
  if (emails.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-500">
        尚未加入 Email。
      </div>
    )
  }

  return (
    <div className="flex flex-wrap gap-2">
      {emails.map((email) => (
        <span
          key={email}
          className={[
            'inline-flex max-w-full items-center gap-2 rounded-full border px-3 py-1 text-sm',
            isValidEmail(email)
              ? 'border-slate-200 bg-slate-50 text-slate-700'
              : 'border-rose-200 bg-rose-50 text-rose-700',
          ].join(' ')}
        >
          <Mail className="size-3.5 shrink-0" />
          <span className="truncate">{email}</span>
          {!isValidEmail(email) ? <Badge variant="destructive">格式</Badge> : null}
          <button type="button" onClick={() => onRemove(email)} className="rounded-full hover:bg-black/5">
            <X className="size-3.5" />
          </button>
        </span>
      ))}
    </div>
  )
}
